import { useEffect, useMemo, useState } from "react";
// Type-only import: the ExcelJS runtime (~1MB) is loaded on demand inside
// `InlineExcel` so it stays out of the bundle until a workbook is rendered.
import type { Cell, Workbook, Worksheet } from "exceljs";
import { Loader2, TriangleAlert } from "lucide-react";
import { cn } from "~/lib/utils";
import type { ArtifactPreview } from "~/services/artifacts";
import { ArtifactShell } from "./ArtifactShell";
import { FileArtifactBody } from "./FileArtifactBody";

// The preview service hands us either a signed URL to a small .xlsx (parsed
// client-side below) or a `too_large` flag with sheet names for workbooks past
// the inline-render cap. There is no server-rendered table payload.
function asRecord(content: ArtifactPreview["content"]): Record<string, unknown> | null {
  return content && typeof content === "object" && !Array.isArray(content)
    ? (content as Record<string, unknown>)
    : null;
}

function workbookUrl(content: ArtifactPreview["content"]): string | null {
  const url = asRecord(content)?.url;
  return typeof url === "string" ? url : null;
}

function isTooLarge(content: ArtifactPreview["content"]): boolean {
  return asRecord(content)?.too_large === true;
}

function sheetNames(content: ArtifactPreview["content"]): string[] {
  const names = asRecord(content)?.sheet_names;
  return Array.isArray(names) ? names.filter((n): n is string => typeof n === "string") : [];
}

interface GridCell {
  colIndex: number;
  text: string;
  rowSpan: number;
  colSpan: number;
}

interface SheetData {
  columns: { index: number; label: string }[];
  rows: { index: number; label: string; cells: GridCell[] }[];
  totalRows: number;
  truncated: boolean;
}

// Column index (1-based) to its spreadsheet letter: 1 -> A, 27 -> AA.
function columnLabel(index: number): string {
  let n = index;
  let label = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    label = String.fromCharCode(65 + rem) + label;
    n = Math.floor((n - 1) / 26);
  }
  return label;
}

// Parse one endpoint of a merge range ("B3") into 1-based row/column numbers.
// ExcelJS always emits well-formed A1 ranges; returns null on anything else so
// a stray range is skipped rather than throwing out of the render path.
function decodeMergeAddress(addr: string): { row: number; col: number } | null {
  const match = /^([A-Z]+)(\d+)$/.exec(addr);
  if (!match) return null;
  let col = 0;
  for (const ch of match[1]) col = col * 26 + (ch.charCodeAt(0) - 64);
  return { row: Number(match[2]), col };
}

// Render a date the way a spreadsheet would rather than as a raw JS Date
// string. Values are read in UTC to match how the workbook stored them.
function formatDate(value: Date): string {
  const date = `${value.getUTCMonth() + 1}/${value.getUTCDate()}/${value.getUTCFullYear()}`;
  if (value.getUTCHours() === 0 && value.getUTCMinutes() === 0 && value.getUTCSeconds() === 0) {
    return date;
  }
  const hh = String(value.getUTCHours()).padStart(2, "0");
  const mm = String(value.getUTCMinutes()).padStart(2, "0");
  return `${date} ${hh}:${mm}`;
}

// ExcelJS `text` gives Excel-like text for strings, numbers, and formulas but
// renders dates as raw JS Date strings, so we format those ourselves.
function cellText(cell: Cell): string {
  const value = cell.value;
  const date =
    value instanceof Date
      ? value
      : value && typeof value === "object" && "result" in value && value.result instanceof Date
        ? value.result
        : null;
  return date ? formatDate(date) : (cell.text ?? "");
}

// Read a worksheet into an Excel-style grid: column letters (A, B, …) across
// the top, row numbers down the side, and formatted cell text. Merged ranges
// (`model.merges`) render as a single spanning cell; the cells they cover are
// dropped. Clamped to `maxRows` for the card, shrinking any span that would run
// past the cut.
function buildSheetData(worksheet: Worksheet | undefined, maxRows: number): SheetData | null {
  if (!worksheet) return null;
  const totalRows = worksheet.rowCount;
  const colCount = worksheet.columnCount;
  if (totalRows === 0 || colCount === 0) return null;

  const truncated = totalRows > maxRows;
  const lastRow = truncated ? maxRows : totalRows;

  const covered = new Set<string>();
  const spans = new Map<string, { rowSpan: number; colSpan: number }>();
  for (const range of worksheet.model.merges ?? []) {
    const [startAddr, endAddr] = range.split(":");
    const start = decodeMergeAddress(startAddr);
    const end = decodeMergeAddress(endAddr);
    if (!start || !end) continue;
    spans.set(`${start.row}:${start.col}`, {
      rowSpan: end.row - start.row + 1,
      colSpan: end.col - start.col + 1,
    });
    for (let r = start.row; r <= end.row; r++) {
      for (let c = start.col; c <= end.col; c++) {
        if (r !== start.row || c !== start.col) covered.add(`${r}:${c}`);
      }
    }
  }

  const columns = [];
  for (let c = 1; c <= colCount; c++) {
    columns.push({ index: c, label: columnLabel(c) });
  }

  const rows = [];
  for (let r = 1; r <= lastRow; r++) {
    const cells: GridCell[] = [];
    for (let c = 1; c <= colCount; c++) {
      if (covered.has(`${r}:${c}`)) continue;
      const span = spans.get(`${r}:${c}`);
      cells.push({
        colIndex: c,
        text: cellText(worksheet.getCell(r, c)),
        rowSpan: span ? Math.min(span.rowSpan, lastRow - r + 1) : 1,
        colSpan: span?.colSpan ?? 1,
      });
    }
    rows.push({ index: r, label: String(r), cells });
  }

  return { columns, rows, totalRows, truncated };
}

// Excel green, used only for the active-cell border and the selected sheet tab.
const EXCEL_GREEN = "#107C41";
const HEADER_CELL =
  "min-w-[3rem] border border-border bg-muted px-2 py-0.5 text-center font-normal text-muted-foreground";
// Excel tints the row/column header of the active cell.
const HEADER_ACTIVE = "bg-[#d6e8dd] text-foreground";
const SELECTED_CELL_STYLE = {
  outline: `1.5px solid ${EXCEL_GREEN}`,
  outlineOffset: "-1.5px",
} as const;

interface SheetGridProps {
  worksheet: Worksheet | undefined;
  maxRows: number;
  heightClassName: string;
}

const SheetGrid = ({ worksheet, maxRows, heightClassName }: SheetGridProps) => {
  const [selected, setSelected] = useState<(GridCell & { r: number }) | null>(null);
  const sheet = useMemo(() => buildSheetData(worksheet, maxRows), [worksheet, maxRows]);

  if (!sheet) {
    return <div className="p-3 text-sm text-muted-foreground">This sheet is empty.</div>;
  }
  const { columns, rows, totalRows, truncated } = sheet;

  // A merged selection lights up every row/column header it spans.
  const colActive = (col: number) =>
    selected != null && col >= selected.colIndex && col < selected.colIndex + selected.colSpan;
  const rowActive = (row: number) =>
    selected != null && row >= selected.r && row < selected.r + selected.rowSpan;

  return (
    <>
      <div className={cn("overflow-auto bg-background", heightClassName)}>
        <table className="border-collapse text-xs tabular-nums">
          <thead>
            <tr>
              <th className={cn(HEADER_CELL, "sticky left-0 top-0 z-20")} />
              {columns.map((col) => (
                <th
                  key={col.index}
                  className={cn(
                    HEADER_CELL,
                    "sticky top-0 z-10",
                    colActive(col.index) && HEADER_ACTIVE
                  )}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.index}>
                <th
                  scope="row"
                  className={cn(
                    HEADER_CELL,
                    "sticky left-0 z-10",
                    rowActive(row.index) && HEADER_ACTIVE
                  )}
                >
                  {row.label}
                </th>
                {row.cells.map((cell) => {
                  const isSelected =
                    selected?.r === row.index && selected?.colIndex === cell.colIndex;
                  return (
                    <td
                      key={cell.colIndex}
                      title={cell.text || undefined}
                      data-selected={isSelected ? "" : undefined}
                      rowSpan={cell.rowSpan}
                      colSpan={cell.colSpan}
                      onClick={() => setSelected({ ...cell, r: row.index })}
                      className="min-w-[3rem] whitespace-nowrap border border-border/70 px-2 py-0.5"
                      style={isSelected ? SELECTED_CELL_STYLE : undefined}
                    >
                      {cell.text}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {truncated && (
        <div className="border-t border-border bg-muted/30 px-2 py-1 text-xs text-muted-foreground">
          Showing the first {maxRows.toLocaleString()} of {totalRows.toLocaleString()} rows — expand
          or download for the full sheet.
        </div>
      )}
    </>
  );
};

interface SheetViewProps {
  workbook: Workbook;
  activeSheet: string;
  onSelect: (sheet: string) => void;
  maxRows: number;
  heightClassName: string;
}

// Excel anchors its sheet tabs along the bottom of the workbook: the active tab
// is white with a bold label and a green underline, inactive tabs sit flush on
// a muted strip.
const SheetView = ({
  workbook,
  activeSheet,
  onSelect,
  maxRows,
  heightClassName,
}: SheetViewProps) => {
  const names = workbook.worksheets.map((ws) => ws.name);
  return (
    <div className="flex flex-col overflow-hidden rounded-md border border-border">
      <SheetGrid
        key={activeSheet}
        worksheet={workbook.getWorksheet(activeSheet)}
        maxRows={maxRows}
        heightClassName={heightClassName}
      />
      {names.length > 1 && (
        <div
          className="flex items-stretch overflow-x-auto border-t border-border bg-muted/40 text-xs"
          role="tablist"
          aria-label="Worksheets"
        >
          {names.map((name) => (
            <button
              key={name}
              type="button"
              role="tab"
              aria-selected={name === activeSheet}
              onClick={() => onSelect(name)}
              className={cn(
                "whitespace-nowrap border-r border-b-2 border-r-border px-3 py-1.5 transition-colors",
                name === activeSheet
                  ? "border-b-[#107C41] bg-background font-semibold text-[#107C41]"
                  : "border-b-transparent text-muted-foreground hover:bg-muted/70"
              )}
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const INLINE_MAX_ROWS = 100;
const EXPANDED_MAX_ROWS = 1000;

// Card for workbooks the preview service deemed too large to render inline. We
// still surface the sheet names (when known) and a prominent download.
const TooLargeExcel = ({ preview }: { preview: ArtifactPreview }) => {
  const names = sheetNames(preview.content);
  return (
    <ArtifactShell preview={preview}>
      <div className="space-y-3">
        <div className="text-sm text-muted-foreground">
          This spreadsheet is too large to preview inline.
        </div>
        {names.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {names.map((name) => (
              <span
                key={name}
                className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground"
              >
                {name}
              </span>
            ))}
          </div>
        )}
        <FileArtifactBody preview={preview} />
      </div>
    </ArtifactShell>
  );
};

const InlineExcel = ({ preview, url }: { preview: ArtifactPreview; url: string }) => {
  const [workbook, setWorkbook] = useState<Workbook | null>(null);
  const [activeSheet, setActiveSheet] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    setWorkbook(null);
    (async () => {
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Failed to download spreadsheet (${response.status})`);
        }
        const { Workbook } = await import("exceljs");
        const parsed = new Workbook();
        await parsed.xlsx.load(await response.arrayBuffer());
        setWorkbook(parsed);
        setActiveSheet(parsed.worksheets[0]?.name ?? null);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("Failed to parse excel artifact:", err);
        setError(err instanceof Error ? err.message : "Failed to read spreadsheet");
      }
    })();
    return () => controller.abort();
  }, [url]);

  const ready = workbook !== null && activeSheet !== null;

  const body = error ? (
    <div className="flex h-24 flex-col items-center justify-center gap-1.5 text-center text-sm text-muted-foreground">
      <TriangleAlert className="size-5 text-muted-foreground/70" aria-hidden />
      {error}
    </div>
  ) : !ready ? (
    <div className="flex h-24 items-center justify-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      Loading spreadsheet…
    </div>
  ) : (
    <SheetView
      workbook={workbook}
      activeSheet={activeSheet}
      onSelect={setActiveSheet}
      maxRows={INLINE_MAX_ROWS}
      heightClassName="max-h-[28rem]"
    />
  );

  return (
    <ArtifactShell
      preview={preview}
      expandedContent={
        ready ? (
          <SheetView
            workbook={workbook}
            activeSheet={activeSheet}
            onSelect={setActiveSheet}
            maxRows={EXPANDED_MAX_ROWS}
            heightClassName="max-h-full"
          />
        ) : undefined
      }
    >
      {body}
    </ArtifactShell>
  );
};

export const ExcelArtifact = ({ preview }: { preview: ArtifactPreview }) => {
  if (isTooLarge(preview.content)) {
    return <TooLargeExcel preview={preview} />;
  }
  const url = workbookUrl(preview.content);
  if (!url) {
    return (
      <ArtifactShell preview={preview}>
        <div className="text-sm text-destructive">Unexpected excel preview shape.</div>
      </ArtifactShell>
    );
  }
  return <InlineExcel preview={preview} url={url} />;
};
